import "./Categories.css";
import React, {memo, useCallback, useState} from "react";
import {DataLoadingStatus, GroupOperation, IndicatorSiblingType} from "../../../Utils/enums";
import {deleteCategory, renameCategory} from "../../../Utils/queries";
import {Category, DivisionAttribute, DivisionFull, Indicator} from "../../../Utils/response-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronUp} from "@fortawesome/free-solid-svg-icons";
import {faEdit, faHashtag, faTrash} from "@fortawesome/free-solid-svg-icons";
import GroupDisplay from "./GroupDisplay";

const CategoryDisplay = ({category, attributes_list, setDivisions, status, setStatus}: {category: Category, attributes_list: DivisionAttribute[], setDivisions: React.Dispatch<React.SetStateAction<DivisionFull[]>>, status: DataLoadingStatus, setStatus: React.Dispatch<React.SetStateAction<DataLoadingStatus>>}) => {
    const [extended, setExtended] = useState<boolean>(false);

    const handleDeleteCategory = useCallback(() => {
        // eslint-disable-next-line no-restricted-globals
        const confirmed = confirm("Do you really want to delete this category and all its rules? This action cannot be undone!");
        if (confirmed) {
            setStatus(DataLoadingStatus.Loading);
            let old_div_list: DivisionFull[] = [];
            setDivisions((prev) => {
              old_div_list = prev;
              return prev.map((div) =>
                div.division_id === category.division_id
                  ? { ...div, categories: div.categories.filter(c => c.category_id !== category.category_id) }
                  : div
              )
            });
            deleteCategory(category.division_id, category.category_id).then(resp => {
                if (resp.errorCode !== undefined || !resp.success){
                    alert(resp.message || `Error: ${resp.errorCode}`);
                    setDivisions(old_div_list);
                } else {
                    alert("Category deleted successfully!")
                }
                setStatus(DataLoadingStatus.Loaded);
            });
        }
    }, [category, setDivisions, setStatus]);

    const handleChangeCSIName = useCallback(() => {
        // eslint-disable-next-line no-restricted-globals
        const new_csi_name = prompt("Are you sure you want to rename a category?\nThis is unusual action.\nEnter the new category name:");
        if (new_csi_name !== null && new_csi_name.length > 0) {
            setStatus(DataLoadingStatus.Loading);
            let old_div_list: DivisionFull[] = [];
            setDivisions((prev) => {
              old_div_list = prev;
              return prev.map((div) =>
                div.division_id === category.division_id
                  ? { ...div, categories: div.categories.map(c =>
                      c.category_id === category.category_id
                          ? {...c, CSI_name: new_csi_name}
                          : c
                  ) }
                  : div
              )
            });
            renameCategory(category.division_id, category.category_id, category.CSI_number, new_csi_name).then(resp => {
                if (resp.errorCode !== undefined || !resp.success) {
                    alert(resp.message || `Error: ${resp.errorCode}`);
                    setDivisions(old_div_list);
                } else {
                    alert("CSI name successfully changed!")
                }
                setStatus(DataLoadingStatus.Loaded);
            });
        }
    }, [category, setDivisions, setStatus]);

    const handleChangeCSINumber = useCallback(() => {
        // eslint-disable-next-line no-restricted-globals
        const new_csi_number = prompt("Are you sure you want to change the CSI number of the category?\nThis is unusual action.\nEnter the new CSI number:");
        if (new_csi_number !== null && new_csi_number.length > 0 && /\d\d\s\d\d\s\d\d/.test(new_csi_number)) {
            setStatus(DataLoadingStatus.Loading);
            let old_div_list: DivisionFull[] = [];
            setDivisions((prev) => {
              old_div_list = prev;
              return prev.map((div) =>
                div.division_id === category.division_id
                  ? { ...div, categories: div.categories.map(c =>
                      c.category_id === category.category_id
                          ? {...c, CSI_number: new_csi_number}
                          : c
                  ) }
                  : div
              )
            });
            renameCategory(category.division_id, category.category_id, new_csi_number, category.CSI_number).then(resp => {
                if (resp.errorCode !== undefined || !resp.success){
                    alert(resp.message || `Error: ${resp.errorCode}`);
                    setDivisions(old_div_list);
                } else {
                    alert("CSI number successfully changed!")
                }
                setStatus(DataLoadingStatus.Loaded);
            });
        }
    }, [category, setDivisions, setStatus]);

    const handleWrapRootGroup = useCallback((sibling_id: number, sibling_type: IndicatorSiblingType) => {
        setDivisions((prev) => prev.map((div) =>
            div.division_id === category.division_id
              ? { ...div, categories: div.categories.map(c => c.category_id === category.category_id
                    ? {...c, indicator_groups: [{indicator_group_id: -1, category_id: category.category_id, parent_group_id: -1, bool_operation: GroupOperation.And, child_groups: c.indicator_groups, child_indicators: [] as Indicator[]}]}
                    : c
                ) }
              : div
          )
        );
    }, [category, setDivisions]);

    const handleCancelWrapRootGroup = useCallback(() => {
        setDivisions((prev) => prev.map((div) =>
            div.division_id === category.division_id
              ? { ...div, categories: div.categories.map(c => c.category_id !== category.category_id
                    ? {...c, indicator_groups: c.indicator_groups[0].child_groups}
                    : c
                ) }
              : div
          )
        );
    }, [category, setDivisions]);

    return (
        <div className={"category-wrapper"}>
            <div className="category-container-head" onClick={() => {
                setExtended(!extended)
            }}>
                <p className="category-name">
                    {category.CSI_name} ({category.CSI_number})
                    {extended && <>
                        <button className="history-action-button" aria-label="Change CSI Name" title="Change CSI Name"
                                onClick={(e) => {e.stopPropagation(); handleChangeCSIName()}}><FontAwesomeIcon icon={faEdit}/></button>
                        <button className="history-action-button" aria-label="Change CSI Name" title="Change CSI Name"
                                onClick={(e) => {e.stopPropagation(); handleChangeCSINumber()}}><FontAwesomeIcon icon={faHashtag}/></button>
                        <button className="history-action-button" aria-label="Delete Category" title="Delete Category"
                                onClick={(e) => {e.stopPropagation(); handleDeleteCategory()}}><FontAwesomeIcon icon={faTrash}/></button>
                    </>}
                </p>
                <div className="chevron-wrapper">
                    <FontAwesomeIcon icon={extended ? faChevronUp : faChevronDown}/>
                </div>
            </div>
            <div className="extendable-category-container" style={{display: extended ? "flex" : "none"}}>
                {
                    (category.indicator_groups.length > 0? category.indicator_groups : [{indicator_group_id: -1, category_id: category.category_id, parent_group_id: -1, bool_operation: GroupOperation.And, child_groups: [], child_indicators: []}])
                        .map(ig => <GroupDisplay key={ig.indicator_group_id} group={ig} division_id={category.division_id} attributes_list={attributes_list} setDivisions={setDivisions} status={status} setStatus={setStatus} parentCancelAddGroup={handleCancelWrapRootGroup} onInitiateGroupCreation={handleWrapRootGroup} />)}
            </div>
        </div>
    );
};


export default memo(CategoryDisplay);